import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within SocketProvider");
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [reconnecting, setReconnecting] = useState(false);
  // socketInstance is tracked in state so context consumers re-render when it changes
  const [socketInstance, setSocketInstance] = useState(null);
  const socketRef = useRef(null);
  // Bumped on every connect attempt and on cleanup so a socket.io-client chunk
  // that resolves after the effect has re-run/unmounted is discarded instead
  // of being wired up as a dangling connection.
  const connectSeqRef = useRef(0);
  const { user, token, loading } = useAuth();

  const connectSocket = useCallback(() => {
    // Already connected — nothing to do
    if (socketRef.current?.connected) {
      return;
    }

    // Resolve token: authContext first, then per-module localStorage fallbacks
    const resolvedToken =
      token ||
      localStorage.getItem("adminToken") ||
      localStorage.getItem("partnerToken") ||
      localStorage.getItem("childcareToken");

    // Resolve userId: authContext first, then decode from JWT
    let resolvedUserId = user?.id;
    if (!resolvedUserId && resolvedToken) {
      try {
        const payload = JSON.parse(atob(resolvedToken.split(".")[1]));
        resolvedUserId = payload.id || payload.userId;
      } catch (_) {}
    }

    // Only defer for authContext-managed logins; if we already have a token
    // from localStorage (e.g. partnerToken), connect immediately.
    if (loading && !resolvedToken) {
      console.log("⏳ Auth loading, deferring socket connection");
      return;
    }

    if (!resolvedToken || !resolvedUserId) {
      console.log("🔒 No credentials — skipping socket connection");
      return;
    }

    // Tear down any existing socket before creating a new one
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocketInstance(null);
    }

    const socketUrl =
      process.env.REACT_APP_SOCKET_URL || "http://localhost:5000";

    const rawToken = resolvedToken.startsWith("Bearer ")
      ? resolvedToken.slice(7)
      : resolvedToken;

    // socket.io-client is loaded on demand — anonymous visitors (the vast
    // majority of homepage traffic) never pay for it in the main bundle.
    const seq = ++connectSeqRef.current;
    import("socket.io-client").then(({ io }) => {
      if (seq !== connectSeqRef.current) return; // stale: unmounted/re-run before this resolved

      console.log("🔌 Connecting to socket server:", socketUrl);

      const sock = io(socketUrl, {
        auth: { token: rawToken },
        transports: ["websocket", "polling"],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      });

      socketRef.current = sock;
      setSocketInstance(sock); // expose to consumers immediately

      sock.on("connect", () => {
        console.log("✅ Socket connected:", sock.id);
        setIsConnected(true);
        setReconnecting(false);
        if (resolvedUserId) sock.emit("register", resolvedUserId);
      });

      sock.on("disconnect", (reason) => {
        console.log("🔴 Socket disconnected:", reason);
        setIsConnected(false);
        if (reason === "io server disconnect") sock.connect();
      });

      sock.on("connect_error", (error) => {
        console.error("❌ Socket connect_error:", error.message);
        setIsConnected(false);
        if (error.message === "Invalid token") {
          sock.disconnect();
          console.warn("Socket auth rejected — session may be stale");
          return;
        }
        setReconnecting(true);
      });

      sock.on("reconnect_attempt", (attempt) => {
        console.log(`🔄 Reconnection attempt ${attempt}`);
      });

      sock.on("reconnect", () => {
        console.log("✅ Socket reconnected");
        setIsConnected(true);
        setReconnecting(false);
        if (resolvedUserId) sock.emit("register", resolvedUserId);
      });

      sock.on("online_users", (data) => {
        const users = Array.isArray(data) ? data : data?.users || [];
        setOnlineUsers(new Set(users));
      });

      sock.on("user_status_changed", ({ userId, isOnline }) => {
        setOnlineUsers((prev) => {
          const next = new Set(prev);
          if (isOnline) next.add(userId);
          else next.delete(userId);
          return next;
        });
      });
    });
  }, [token, user?.id, loading]);

  // Re-run whenever auth state changes (login, logout, or auth finish loading)
  useEffect(() => {
    connectSocket();

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
      // mutable cancellation counter, not a captured DOM/value snapshot
      connectSeqRef.current++; // invalidate any socket.io-client import still in flight
      if (socketRef.current) {
        console.log("🧹 Cleaning up socket");
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocketInstance(null);
        setIsConnected(false);
      }
    };
  }, [connectSocket]);

  const emit = useCallback((event, data) => {
    // Use the live socket.io .connected property — more reliable than React state
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
      return true;
    }
    console.warn(`⚠️ Cannot emit ${event}: socket not connected`);
    return false;
  }, []); // no deps — reads ref directly, avoids stale-closure issues

  const on = useCallback((event, callback) => {
    if (socketRef.current) {
      socketRef.current.on(event, callback);
      return () => socketRef.current?.off(event, callback);
    }
    return () => {};
  }, []);

  const off = useCallback((event, callback) => {
    if (socketRef.current) socketRef.current.off(event, callback);
  }, []);

  const reconnect = useCallback(() => {
    console.log("🔄 Manual reconnect requested");
    connectSocket();
  }, [connectSocket]);

  const value = {
    socket: socketInstance, // state-backed — consumers re-render correctly
    isConnected,
    onlineUsers,
    reconnecting,
    emit,
    on,
    off,
    reconnect,
    isUserOnline: useCallback(
      (userId) => onlineUsers.has(userId),
      [onlineUsers],
    ),
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
